import { CardStatus } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../contexts/data-context/use-data';
import { CardView } from '../../../types/entity-types';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';

const CARDS_CANCEL_API_PATH = '/api/cards/cancel';

type CancelCardRequest = {
  cardId: string;
};

export const useCancelCard = () => {
  const { data, mutations } = useData();

  const { trigger, isMutating } = useSWRMutation<CardView, Error, string, CancelCardRequest>(
    CARDS_CANCEL_API_PATH,
    postSwrFetcher,
  );

  const cancelCard = async (cardId: string): Promise<CardView | undefined> => {
    try {
      const cancelledCard = await trigger({ cardId });

      if (cancelledCard) {
        toast.success('Card cancelled successfully');

        if (data?.cards) {
          await mutations.mutateCache?.({
            cards: data.cards.map((card) => (card.id === cardId ? { ...card, status: CardStatus.CANCELLED } : card)),
          });
        }
      }

      return cancelledCard;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          cardId,
          context: 'cancelCard',
        },
      });
      toast.error('Failed to cancel card. Please try again.');
      throw error;
    }
  };

  return {
    cancelCard,
    isCancelling: isMutating,
  };
};
